import { useCallback, useEffect, useState } from "react";
import { Avatar } from "../components/ui/Avatar";
import { AppShell } from "../components/layout/AppShell";
import { Card } from "../components/ui/Primitives";
import { Button } from "../components/ui/Button";
import { Select, Textarea } from "../components/ui/Fields";
import { Modal } from "../components/ui/Modal";
import { useToast } from "../components/ui/Toast";
import { useAuth } from "../auth/AuthContext";
import { apiRequest, ApiError } from "../api/client";

/**
 * Round 50 — Post Review over routes/admin/post-review.js: the "Post
 * Approval Required" ON/OFF switch and the review queue. Who may post
 * (Wealth Level 5+) and each post's resulting status are decided by the
 * database, not by this page.
 */
interface ReviewPost {
  id: number; caption: string | null; mediaUrl: string | null; mediaType: string | null; createdAt: string;
  status: "pending" | "approved" | "rejected"; reviewedAt: string | null; rejectReason: string | null;
  author: { id: string; userId: number | null; username: string; avatarUrl: string | null; wealthLevel: number };
}

export function PostReviewPage() {
  const { session } = useAuth();
  const [required, setRequired] = useState<boolean | null>(null);
  const [status, setStatus] = useState<"pending" | "approved" | "rejected">("pending");
  const [posts, setPosts] = useState<ReviewPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [rejecting, setRejecting] = useState<ReviewPost | null>(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const { showToast } = useToast();
  const isSuper = session?.role === "super_admin";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, list] = await Promise.all([
        apiRequest<{ approvalRequired: boolean }>("/admin/post-review/settings"),
        apiRequest<{ posts: ReviewPost[] }>("/admin/post-review", { params: { status } }),
      ]);
      setRequired(s.approvalRequired);
      setPosts(list.posts);
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not load", "error");
    } finally {
      setLoading(false);
    }
  }, [status, showToast]);
  useEffect(() => { void load(); }, [load]);

  async function toggle() {
    if (required === null) return;
    try {
      const res = await apiRequest<{ approvalRequired: boolean }>("/admin/post-review/settings", { method: "PATCH", body: { approvalRequired: !required } });
      setRequired(res.approvalRequired);
      showToast(`Post approval is now ${res.approvalRequired ? "ON" : "OFF"}`, "success");
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not save", "error");
    }
  }
  async function decide(p: ReviewPost, action: "approve" | "reject", why?: string) {
    setBusy(true);
    try {
      await apiRequest(`/admin/post-review/${p.id}/${action}`, { method: "POST", body: action === "reject" ? { reason: why ?? "" } : {} });
      showToast(action === "approve" ? "Post approved — now public" : "Post rejected", "success");
      setRejecting(null); setReason("");
      await load();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not update post", "error");
      await load();
    } finally {
      setBusy(false);
    }
  }

  return (
    <AppShell title="Post Review">
      <Card className="mb-4 max-w-2xl p-5">
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <p className="font-semibold text-ink">Post Approval Required: {required === null ? "…" : required ? "ON" : "OFF"}</p>
            <p className="text-xs text-ink-faint">
              Only users with Wealth Level 5 or higher can post. ON: their new posts are held as Pending Review and are not public
              until approved. OFF: they are public immediately.
            </p>
          </div>
          <Button onClick={toggle} disabled={required === null || !isSuper} variant={required ? "secondary" : "primary"}>
            {required ? "Turn OFF" : "Turn ON"}
          </Button>
        </div>
        {!isSuper && <p className="mt-2 text-xs text-ink-faint">Only a super admin can change this setting.</p>}
      </Card>

      <div className="mb-3 max-w-xs">
        <Select label="Show" value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
          <option value="pending">Pending review</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </Select>
      </div>

      {loading ? <p className="text-sm text-ink-faint">Loading…</p> : posts.length === 0 ? <p className="text-sm text-ink-faint">No {status} posts.</p> : (
        <div className="max-w-2xl space-y-3">
          {posts.map((p) => (
            <Card key={p.id} className="p-4">
              <div className="mb-2 flex items-center gap-3">
                <Avatar src={p.author.avatarUrl} className="h-9 w-9 rounded-full object-cover" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-ink">{p.author.username || `user_${p.author.userId}`}</p>
                  <p className="font-mono text-xs text-ink-faint">ID {p.author.userId ?? "—"} · Wealth Lv {p.author.wealthLevel}</p>
                </div>
                <div className="text-right text-xs text-ink-faint">
                  <p>Submitted {new Date(p.createdAt).toLocaleString()}</p>
                  <p className="font-semibold uppercase">{p.status}{p.reviewedAt ? ` · ${new Date(p.reviewedAt).toLocaleString()}` : ""}</p>
                </div>
              </div>
              {p.caption && <p className="mb-2 whitespace-pre-wrap text-sm text-ink">{p.caption}</p>}
              {p.mediaUrl && (p.mediaType === "video"
                ? <video src={p.mediaUrl} controls className="mb-2 max-h-80 rounded" />
                : <img src={p.mediaUrl} alt="" className="mb-2 max-h-80 rounded object-contain" />)}
              {p.rejectReason && <p className="mb-2 text-xs text-danger">Rejected: {p.rejectReason}</p>}
              {p.status === "pending" && (
                <div className="flex gap-2">
                  <Button onClick={() => void decide(p, "approve")} disabled={busy}>Approve</Button>
                  <Button variant="danger" onClick={() => { setRejecting(p); setReason(""); }} disabled={busy}>Reject</Button>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      <Modal
        isOpen={rejecting !== null}
        onClose={() => !busy && setRejecting(null)}
        title="Reject post"
        footer={
          <>
            <Button variant="ghost" onClick={() => setRejecting(null)} disabled={busy}>Cancel</Button>
            <Button variant="danger" onClick={() => rejecting && void decide(rejecting, "reject", reason)} isLoading={busy}>Reject</Button>
          </>
        }
      >
        <p className="mb-3 text-sm text-ink-muted">
          {rejecting ? `Reject this post by ${rejecting.author.username}? It stays hidden from everyone.` : ""}
        </p>
        <Textarea label="Reason (optional)" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
      </Modal>
    </AppShell>
  );
}
